import { useMutation } from "@apollo/client";
import ExpandMore from "@mui/icons-material/ExpandMore";
import PersonAddAlt1 from "@mui/icons-material/PersonAddAlt1";
import PersonRemove from "@mui/icons-material/PersonRemove";
import { Button, Divider, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { MultipleUserPicker, UserOption } from "components/MultipleUserPicker";
import { useGridSelection } from "hooks/omnisearch-v2";
import { useCurrentUser } from "hooks/useCurrentUser";
import {
  HARMONIZED_VIOLATION_STATS,
  HARMONIZED_VIOLATIONS,
  SET_HARMONIZED_VIOLATION_ASSIGNEES,
  SET_HARMONIZED_VIOLATION_STAFF_ASSIGNEES,
} from "hooks/useHarmonizedViolations";
import { useState } from "react";

import { type ViolationRow } from "./columns";
import { firstUserId } from "./violationDefaultAssignees";

/** Which assignee a bulk action targets. */
type BulkAssignMode = "assignee" | "staffAssignee";

/** Mirrors the server's per-call cap (MAX_BULK_VIOLATION_IDS) for a clearer client message. */
const MAX_BULK_ASSIGN = 500;

/**
 * Picks one user (single-select) for a bulk assignment. Staff mode searches
 * Encamp staff; customer mode searches the tenant's users — the same split the
 * violation drawer's two override pickers use. Snapshotted rows are assigned on
 * confirm, so a selection change behind the dialog cannot retarget the write.
 */
function BulkAssignDialog({
  mode,
  count,
  saving,
  onAssign,
  onClose,
}: {
  mode: BulkAssignMode;
  count: number;
  saving: boolean;
  onAssign: (userId: string) => void;
  onClose: () => void;
}) {
  const [selected, setSelected] = useState<UserOption[] | null>(null);
  const userId = firstUserId(selected);
  const noun = count === 1 ? "violation" : "violations";

  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      title={mode === "staffAssignee" ? "Assign staff to violations" : "Assign violations"}
      primaryActionButtonProps={{
        disabled: !userId || saving,
        onClick: () => userId && onAssign(userId),
        saveText: `Assign ${count} ${noun}`,
        startIcon: null,
      }}
    >
      <MultipleUserPicker
        label={mode === "staffAssignee" ? "Staff assignee" : "Assignee"}
        multiple={false}
        staffOnly={mode === "staffAssignee"}
        value={selected}
        disabled={saving}
        onChange={setSelected}
      />
    </Dialog>
  );
}

/**
 * The violations grid's Bulk Actions dropdown: assign or clear the customer
 * assignee across the selected violations, plus the staff assignee for staff
 * users. Rendered as a grid command button, disabled until rows are selected.
 * Mirrors the staff Work queue's bulk-actions pattern. Must live inside the
 * grid's {@link GridSelectionProvider}.
 */
export function ViolationBulkActions({ tenantId }: { tenantId: string }) {
  const { isStaff } = useCurrentUser();
  // Ids, not rows: a selection can outlive the page it was made on (the grid
  // keeps off-page selections), and getSelectedRows() returns undefined for
  // those — reading `.id` off them would throw. Ids are always real.
  const { selectedCount, getSelectedIds, clearSelection } = useGridSelection<ViolationRow>();
  const alerts = useAlerts();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  // Ids captured when the dialog opened, so the mutation applies to that
  // snapshot rather than a selection the user has since changed.
  const [dialog, setDialog] = useState<{ mode: BulkAssignMode; ids: string[] } | null>(null);

  const menuOpen = Boolean(menuAnchor);
  const closeMenu = () => setMenuAnchor(null);

  // A bulk change shifts the by-assignee donut AND the filtered row set (rows
  // assigned away from an active "assignee:" filter must leave the table), and
  // Apollo can recompute neither from the returned rows.
  const refetchQueries = [HARMONIZED_VIOLATIONS, HARMONIZED_VIOLATION_STATS];
  const [setAssignees, { loading: savingAssignee }] = useMutation(
    SET_HARMONIZED_VIOLATION_ASSIGNEES,
    { refetchQueries }
  );
  const [setStaffAssignees, { loading: savingStaff }] = useMutation(
    SET_HARMONIZED_VIOLATION_STAFF_ASSIGNEES,
    { refetchQueries }
  );
  const saving = savingAssignee || savingStaff;

  const run = async (mode: BulkAssignMode, ids: string[], userId: string | null) => {
    if (ids.length === 0) {
      return;
    }
    // Match the server's per-call cap, with a specific message — otherwise the
    // request fails wholesale and the user is told only to "try again".
    if (ids.length > MAX_BULK_ASSIGN) {
      alerts.error(
        `Select at most ${MAX_BULK_ASSIGN} violations at a time (you selected ${ids.length}).`
      );
      return;
    }
    try {
      // Report the server's count, not the selection's — the backend skips rows
      // the caller can't see (held / out of tenant), so a stale selection would
      // otherwise claim more than actually changed.
      let updated: number;
      if (mode === "assignee") {
        const { data } = await setAssignees({ variables: { tenantId, ids, userId } });
        updated = data?.setHarmonizedViolationAssignees.length ?? 0;
      } else {
        const { data } = await setStaffAssignees({ variables: { tenantId, ids, userId } });
        updated = data?.setHarmonizedViolationStaffAssignees.length ?? 0;
      }
      if (updated === 0) {
        // Keep the selection so the user can investigate why nothing changed.
        alerts.info("No violations were updated.");
        return;
      }
      const noun = updated === 1 ? "violation" : "violations";
      const who = mode === "staffAssignee" ? "staff assignee" : "assignee";
      if (userId) {
        alerts.success(
          mode === "staffAssignee"
            ? `Set the staff assignee on ${updated} ${noun}`
            : `Assigned ${updated} ${noun}`
        );
      } else {
        alerts.success(`Cleared the ${who} on ${updated} ${noun}`);
      }
      clearSelection();
    } catch (e) {
      // Leave the selection intact so the user can retry.
      alerts.error("Could not update the assignees. Try again.", e);
    }
  };

  return (
    <>
      <Button
        id="violation-bulk-actions-button"
        aria-controls={menuOpen ? "violation-bulk-actions-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={menuOpen ? "true" : undefined}
        onClick={(event) => setMenuAnchor(event.currentTarget)}
        endIcon={<ExpandMore />}
        variant="contained"
        color="secondary"
        disabled={selectedCount === 0 || saving}
      >
        Bulk Actions
      </Button>
      <Menu
        id="violation-bulk-actions-menu"
        anchorEl={menuAnchor}
        open={menuOpen}
        onClose={closeMenu}
        // Prevent the menu from affecting body scroll, which trips the grid's ResizeObserver.
        disableScrollLock
        MenuListProps={{ "aria-labelledby": "violation-bulk-actions-button" }}
      >
        <MenuItem
          onClick={() => {
            closeMenu();
            setDialog({ mode: "assignee", ids: getSelectedIds().map(String) });
          }}
        >
          <ListItemIcon>
            <PersonAddAlt1 fontSize="small" />
          </ListItemIcon>
          <ListItemText>Assign…</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            closeMenu();
            void run("assignee", getSelectedIds().map(String), null);
          }}
        >
          <ListItemIcon>
            <PersonRemove fontSize="small" />
          </ListItemIcon>
          <ListItemText>Clear assignee</ListItemText>
        </MenuItem>
        {isStaff
          ? [
              <Divider key="staff-divider" />,
              <MenuItem
                key="assign-staff"
                onClick={() => {
                  closeMenu();
                  setDialog({ mode: "staffAssignee", ids: getSelectedIds().map(String) });
                }}
              >
                <ListItemIcon>
                  <PersonAddAlt1 fontSize="small" />
                </ListItemIcon>
                <ListItemText>Assign staff…</ListItemText>
              </MenuItem>,
              <MenuItem
                key="clear-staff"
                onClick={() => {
                  closeMenu();
                  void run("staffAssignee", getSelectedIds().map(String), null);
                }}
              >
                <ListItemIcon>
                  <PersonRemove fontSize="small" />
                </ListItemIcon>
                <ListItemText>Clear staff assignee</ListItemText>
              </MenuItem>,
            ]
          : null}
      </Menu>
      {dialog ? (
        <BulkAssignDialog
          mode={dialog.mode}
          count={dialog.ids.length}
          saving={saving}
          onAssign={(userId) => {
            const { mode, ids } = dialog;
            // Keep the dialog mounted through the mutation so its `saving` state
            // (disabled button + spinner) is reachable; close it once done. `run`
            // catches its own errors, so `finally` always dismisses.
            void run(mode, ids, userId).finally(() => setDialog(null));
          }}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </>
  );
}
